import update from 'immutability-helper';
import { reject, set, merge } from 'lodash/fp';
import { ASSIGN } from 'constants/actionTypes';
import { ORDER, COLUMNS, TYPES } from 'constants/config';
import REGEX from 'constants/regex';

const initialState = {
  activeStep: 'CONTENT',
  data: [],
  journeyData: [],
  journeySort: { order: ORDER.DESC, type: COLUMNS.LAST_MODIFIED },
  contentType: 0,
  pagination: {},
  params: {
    query: '',
    sort: { order: ORDER.DESC, type: COLUMNS.LAST_MODIFIED },
    filter: {
      type: [TYPES.LESSON, TYPES.PATH, TYPES.JOURNEY],
      allTypes: [TYPES.LESSON, TYPES.PATH],
    },
  },
  selection: {
    content: { data: [], journeyData: [] },
    assignees: { count: 0, data: [] },
  },
  schedule: {
    assignDate: undefined, // conditionally set to null upon opening "Scheduling"
    dueDate: undefined, // conditionally set to null upon opening "Scheduling"
    recurring: false,
    recurringOptions: {
      assignOnAmount: null,
      assignOnUnit: null,
      dueDateAmount: null,
      dueDateUnit: null,
    },
  },
  existingAssignments: {
    sendReminder: false,
    reassignCompleted: false,
  },
  message: '',
  loading: false,
  journeyLoading: true,
  success: true,
};

const initialAssigneesState = {
  params: {
    query: '',
    sort: { order: ORDER.DESC, type: COLUMNS.LAST_MODIFIED },
    filter: {
      type: ['User', 'Group'],
      allTypes: ['User', 'Group'],
    },
  },
};

/**
 * Add a new type to initialState.params.filter.type
 * @param {String} type Type to be added to the filter[type] array
 */
const addTypeToTypesFilter = type => {
  const typesSet = new Set([...initialState.params.filter.type, type]);
  initialState.params.filter.type = Array.from(typesSet);
  initialState.params.filter.allTypes = Array.from(typesSet);
};

export const isContent = item => REGEX.CONTENT.test(item.type);
export const isAssignee = item => REGEX.ASSIGNEES.test(item.type);

const assignReducer = (state, action) => {
  // helper
  const assign = changes => ({ ...state, ...changes });

  switch (action.type) {
    case ASSIGN.STEP.SET_ACTIVE:
      return { ...state, activeStep: action.step };

    case ASSIGN.CONTENT.SET_TYPE:
      return { ...state, contentType: action.contentType };

    case ASSIGN.DATA.REQUEST:
      return assign({ loading: true });

    case ASSIGN.DATA.RESET:
      return assign({
        data: initialState.data,
        journeySort: { order: ORDER.DESC, type: COLUMNS.LAST_MODIFIED },
        pagination: initialState.pagination,
        params:
          state.activeStep === 'ASSIGNEES'
            ? initialAssigneesState.params
            : initialState.params,
      });

    case ASSIGN.DATA.REQUEST_SUCCESS: {
      const { data, ...pagination } = action.payload;
      return assign({ data, pagination, success: true, loading: false });
    }

    case ASSIGN.DATA.REQUEST_JOURNEYS_SUCCESS: {
      const journeyData = action.payload;

      return assign({ journeyData, journeyLoading: false });
    }

    case ASSIGN.DATA.REQUEST_NEXT_PAGE_SUCCESS: {
      const { data, ...pagination } = action.payload;
      return assign({
        data: state.data.concat(data),
        pagination,
        success: true,
        loading: false,
      });
    }

    case ASSIGN.PARAMS.UPDATE_QUERY:
      return set('params.query', action.query)(state);

    case ASSIGN.PARAMS.UPDATE_SORT:
      return set('params.sort', action.option)(state);

    case ASSIGN.PARAMS.UPDATE_FILTER_TYPE:
      return set('params.filter.type', action.values)(state);

    case ASSIGN.PARAMS.UPDATE_JOURNEY_SORT:
      return { ...state, journeySort: action.option };

    case ASSIGN.ITEM.SELECT: {
      const { item } = action;
      if (item.length >= 1) {
        const content = [];
        const assignees = [];

        item.forEach(obj => {
          if (isContent(obj)) {
            content.push(obj);
          } else if (isAssignee(obj)) {
            assignees.push(obj);
          }
        });

        if (content.length >= 1) {
          return update(state, {
            selection: { content: { data: { $unshift: content } } },
          });
        }
        return update(state, {
          selection: { assignees: { data: { $unshift: assignees } } },
        });
      }
      if (isContent(item)) {
        if (item.type === TYPES.JOURNEY) {
          state.journeyData.find(x => x.id === item.id).selected = true;
          assign({ journeyData: [...state.journeyData] });
          return update(state, {
            selection: {
              content: {
                journeyData: { $unshift: [item] },
                data: { $unshift: [item] },
              },
            },
          });
        }
        return update(state, {
          selection: { content: { data: { $unshift: [item] } } },
        });
      }
      if (isAssignee(item)) {
        return update(state, {
          selection: { assignees: { data: { $unshift: [item] } } },
        });
      }
      return state;
    }

    case ASSIGN.ITEM.DESELECT: {
      const { item } = action;
      if (isContent(item)) {
        const { data } = state.selection.content;
        if (item.type === TYPES.JOURNEY) {
          delete state.journeyData.find(x => x.id === item.id).selected;
          assign({ journeyData: [...state.journeyData] });
          return update(state, {
            selection: {
              content: {
                journeyData: { $set: reject(item, data) },
                data: { $set: reject(item, data) },
              },
            },
          });
        }
        return update(state, {
          selection: { content: { data: { $set: reject(item, data) } } },
        });
      }
      if (isAssignee(item)) {
        const { data } = state.selection.assignees;
        return update(state, {
          selection: { assignees: { data: { $set: reject(item, data) } } },
        });
      }
      return state;
    }

    case ASSIGN.ITEM.SET_ASSIGNEES_COUNT:
      return set('selection.assignees.count', action.count)(state);

    case ASSIGN.SCHEDULE.UPDATE: {
      const updateSchedule = merge(state.schedule);
      return {
        ...state,
        schedule: updateSchedule(action.changes),
      };
    }

    case ASSIGN.REVIEW.UPDATE_MESSAGE:
      return assign({ message: action.body });

    case ASSIGN.REVIEW.UPDATE_EXISTING_ASSIGNMENT_OPTION:
      return update(state, {
        existingAssignments: { $merge: action.option },
      });

    case ASSIGN.SCHEDULE.STEP_SELECT: {
      const { schedule } = action;
      return update(state, {
        schedule: { $merge: schedule },
      });
    }
    default: {
      const { features } = state.meta;
      if (features.resource_experiment) addTypeToTypesFilter(TYPES.RESOURCE);

      return { ...state, ...initialState };
    }
  }
};

export default assignReducer;
